export interface BlogPost {
  slug: string;
  title: string;
  subtitle: string;
  category: string;
  categoryColor: string;
  publishedDate: string;
  readingTime: string;
  author: string;
  authorRole: string;
  heroIcon: string;
  sections: BlogSection[];
  /** Absolute path under /public for the LinkedIn / OG share card (1200×1200 PNG). */
  ogImage?: string;
  /** Optional override for the share-card description. Falls back to `subtitle`. */
  ogDescription?: string;
}

export interface BlogSection {
  type:
    | 'paragraph'
    | 'heading2'
    | 'heading3'
    | 'bullets'
    | 'ordered'
    | 'divider'
    | 'callout'
    | 'scenario';
  content?: string;
  items?: string[];
  /** For callout: 'insight' | 'warning' | 'example'. For scenario: unused. */
  variant?: 'insight' | 'warning' | 'example';
}

export const blogPosts: BlogPost[] = [
  // ── Why Medical Record Review Is the Biggest Hidden Bottleneck ────────
  {
    slug: 'medical-record-review-hidden-bottleneck',
    title:
      'Why medical record review is the biggest hidden bottleneck in medico-legal work',
    subtitle:
      'A consultant neurologist and expert witness on the silent overhead that delays expert opinions, case strategy and decisions long before clinical analysis can begin.',
    category: 'Practitioner Perspective',
    categoryColor: 'amber',
    publishedDate: '2026-05-05',
    readingTime: '9 min read',
    author: 'Dr Khaled Abdel-Aziz PhD FRCP',
    authorRole: 'Consultant Neurologist & Medical Co-founder, ClinLexis',
    heroIcon: 'file-search',
    ogImage: '/og/medical-record-review-hidden-bottleneck.png',
    sections: [
      {
        type: 'paragraph',
        content:
          'As a consultant neurologist working in clinical practice and as an expert witness, a significant part of my work involves reviewing medical records in detail.',
      },
      {
        type: 'paragraph',
        content:
          'Once I have been instructed to provide an expert opinion in any personal injury or clinical negligence matter by a firm of solicitors, there is a need to understand the chronology of events clearly. Understanding what happened, when it happened, and how those events relate to one another is essential before any opinion can be formed; whether on breach of duty, causation or prognosis.',
      },
      {
        type: 'paragraph',
        content:
          'In straightforward cases, this process can be relatively quick and painless, but the reality is that the record review for claimants who have suffered serious or catastrophic injuries, or those with complex medical backgrounds that pre-date the index event, is usually anything but straightforward.',
      },
      {
        type: 'heading2',
        content: 'The reality of modern medical records',
      },
      {
        type: 'paragraph',
        content:
          'In many medico-legal cases today, the sheer volume of documentation that needs to be reviewed can be a considerable hurdle. It is not unusual for me to be asked to review 2,000–4,000 pages of records, and in rare cases I’ve been asked to review bundles exceeding 12,000 pages. Fortunately, it has been a few years since I have had to receive the boxes of lever arch files containing medical records that used to overtake my offices when I first started out in medico-legal practice. But, nonetheless, even electronic records, although more accessible, present their own challenges. Often, the information is fragmented, sometimes repetitive, occasionally incomplete, and regularly formatted in ways that demand careful interpretation. The task of reviewing medical records, therefore, extends beyond simply reading the material; it requires extracting meaningful information from a complex and disorganised collection of documents.',
      },
      {
        type: 'heading2',
        content: 'The work behind the opinion',
      },
      {
        type: 'paragraph',
        content:
          'What is less visible to those outside the process is how much work is required before any formal report is written.',
      },
      {
        type: 'paragraph',
        content:
          'For most experts and legal professionals, this involves:',
      },
      {
        type: 'bullets',
        items: [
          'Reading through large volumes of unstructured material',
          'Identifying relevant clinical events',
          'Establishing a reliable timeline',
          'Cross-referencing entries across different sources',
          'Distinguishing between material and non-material information',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Only once this groundwork has been completed can the meaningful analysis begin. This process is time-consuming, and it requires sustained concentration. Much of the effort at this stage of the process lies in extracting and organising information, rather than in applying specialist clinical or legal judgement.',
      },
      {
        type: 'heading2',
        content: 'The hidden bottleneck',
      },
      {
        type: 'paragraph',
        content:
          'In my experience, this stage of the workflow is one of the most significant bottlenecks in medico-legal practice. Typically, it delays:',
      },
      {
        type: 'bullets',
        items: [
          'Experts forming opinions',
          'Solicitors assessing the merits of a case',
          'Strategic decisions being made',
        ],
      },
      {
        type: 'paragraph',
        content:
          'In complex cases, it can take many hours, sometimes even several days, before the factual chronology is sufficiently clear to proceed to forming a clinical opinion; this is simply a reflection of the scale and nature of the task.',
      },
      {
        type: 'heading2',
        content: 'Is this still sustainable in the long term?',
      },
      {
        type: 'paragraph',
        content:
          'What I have seen over the past 25+ years working within the NHS is that medical record volumes have only continued to rise, as advances in medical practice result in more frequent and detailed investigations of symptoms, and healthcare providers are required to produce more documentation from clinical encounters.',
      },
      {
        type: 'paragraph',
        content:
          'Meanwhile, patients have also become more mobile and often move their care between different NHS providers. As a result, records are frequently dispersed across multiple systems, and as yet, the NHS has still not succeeded in solving the problems of medical record migration.',
      },
      {
        type: 'paragraph',
        content:
          'At the same time, medico-legal work faces tighter time and cost constraints, faster turnaround expectations, and more complex cases. In this context, workflows that rely on manual information extraction and organisation will, in my view, become increasingly unsustainable.',
      },
      {
        type: 'heading2',
        content: 'Where technology has (and hasn’t) helped',
      },
      {
        type: 'paragraph',
        content:
          'There have been improvements in how medical records are stored and accessed. Electronic records have made documentation more available, and digital bundles have replaced paper in many settings. However, accessibility is not the same as usability. The fundamental tasks of reading, extracting, organising, and interpreting medical records remain largely unchanged. In that sense, the majority of medico-legal work still relies on processes that have not evolved significantly, despite the dramatic increase in data volume.',
      },
      {
        type: 'heading2',
        content: 'A shift that feels increasingly necessary',
      },
      {
        type: 'paragraph',
        content:
          'In other areas of law, similar challenges have led to the adoption of new approaches. Electronic disclosure transformed how large document sets are managed in commercial litigation. What was once a manual, labour-intensive process is now supported by structured tools that assist with searching, filtering, and reviewing large volumes of information.',
      },
      {
        type: 'paragraph',
        content:
          'Medical record review may be approaching a similar point. The aim will not be to replace professional judgement. That remains central. But there is a growing recognition that the way we interact with large volumes of information may need to change.',
      },
      {
        type: 'heading2',
        content: 'A practical perspective',
      },
      {
        type: 'paragraph',
        content:
          'From a clinical and medico-legal standpoint, the most valuable work will always lie not in locating information, but in applying deep specialist knowledge and experience in interpreting it.',
      },
      {
        type: 'paragraph',
        content:
          'Understanding the significance of a clinical decision, assessing whether there has been a delay in diagnosis, evaluating the likely trajectory of a condition — these are the tasks that require expertise and experience.',
      },
      {
        type: 'paragraph',
        content:
          'If more of the preliminary work of organising and structuring information could be made more efficient, it would allow medical expert witnesses to focus more of their time on those higher-value activities.',
      },
      {
        type: 'heading2',
        content: 'Looking ahead',
      },
      {
        type: 'paragraph',
        content:
          'Medical record review will always be a central part of medico-legal practice. That is unlikely to change. What may change is how that review is conducted.',
      },
      {
        type: 'paragraph',
        content:
          'As the volume of data continues to grow, and as expectations around efficiency and turnaround increase, there is a clear need to consider how workflows can evolve to better support both the medical and legal professionals working in this space.',
      },
      {
        type: 'paragraph',
        content:
          'Within the current model, highly skilled individuals are spending large amounts of time extracting and organising information manually. While this approach has served the profession well, it is placing workflows under increasing pressure. Recognising that pressure is the first step.',
      },
      {
        type: 'heading2',
        content: 'Final thought',
      },
      {
        type: 'paragraph',
        content:
          'In many ways, the challenge is not a lack of expertise. It is the volume of information that expertise must be applied to. The question, therefore, is not whether medical record review is important — it clearly is, and always will be.',
      },
      {
        type: 'paragraph',
        content:
          'The question is whether the way we approach it today is the most effective way to do so in the future.',
      },
    ],
  },

  // ── The AI tool exposing your firm right now ────────────────────────
  {
    slug: 'shadow-ai-consumer-chatbots',
    title:
      'The AI tool exposing your firm right now probably isn’t the one in the contract',
    subtitle:
      'Why the consumer chatbots already in use across the profession are the AI exposure most procurement processes don’t catch.',
    category: 'Security & Compliance',
    categoryColor: 'red',
    publishedDate: '2026-05-05',
    readingTime: '8 min read',
    author: 'Ahmmad Youssef, PhD',
    authorRole: 'Technical Co-founder, ClinLexis',
    heroIcon: 'shield-check',
    ogImage: '/og/shadow-ai-consumer-chatbots.png',
    sections: [
      {
        type: 'paragraph',
        content:
          'Most of the AI conversations I have had with solicitors and clinicians over the past year have been about vendors. Which platform to choose. What questions to ask. How to evaluate a tool. These are sensible conversations, and I am pleased the profession is having them.',
      },
      {
        type: 'paragraph',
        content:
          'What strikes me is that they tend to skip past the AI tool people are actually using. It is rarely the one in the contract. It is the one already open in another browser tab.',
      },
      {
        type: 'paragraph',
        content:
          'The behaviour I want to talk about is using a free, consumer chatbot (ChatGPT, Claude, Gemini, Copilot) to do a piece of professional work that touches client information or patient records. Summarising a discharge letter. Drafting a section of a witness statement. Making sense of a complex GP record before a clinic appointment. The people doing this are not negligent. They are responding to a tooling gap. But the architecture beneath those consumer tools was not designed for the data being pasted in, and that gap is now the most underestimated AI risk in medico-legal work.',
      },
      {
        type: 'heading2',
        content: 'What’s actually happening',
      },
      {
        type: 'paragraph',
        content: 'Three things tell us this is not a hypothetical risk.',
      },
      {
        type: 'paragraph',
        content:
          'The first is the regulatory response. Within the past two years, the Solicitors Regulation Authority, the Bar Standards Board and the General Medical Council have each issued guidance specifically on the use of generative AI by their members. The SRA’s 2023 Risk Outlook on Technology and Innovation called out generative AI as a live area of concern and reminded firms of their existing confidentiality and supervision duties. Regulators do not write rules against theoretical problems; they write them against behaviours they are already seeing.',
      },
      {
        type: 'paragraph',
        content:
          'The second is the surveys. Industry reports from the Law Society and from the major legal publishers in 2024 consistently found that a substantial proportion of UK fee-earners were using generative AI tools at least weekly, with the figures particularly high among trainees and junior associates. Surveys of NHS clinicians over the same period have found similar patterns of personal experimentation, particularly in primary care. These figures cover all generative AI use, not only consumer chatbots, but the consumer share is unlikely to be small given how much easier consumer products are to reach than vetted enterprise tools. The directly observable evidence is strongest for solicitors. For expert witnesses specifically, we are inferring from adjacent professions and incentives rather than from dedicated surveys.',
      },
      {
        type: 'paragraph',
        content:
          'The third is the courts. Ayinde and Al-Haroun, decided last year, exist because legal professionals used consumer AI tools to draft material that was then placed before a UK court without verification. In the US, Mata v. Avianca (2023) and several follow-on matters show the same pattern. These are the cases that became public; the cases that did not are, by definition, harder to count.',
      },
      {
        type: 'paragraph',
        content:
          'Taken together, this settles the question of whether shadow AI use is happening in the profession. It is. The useful question is what to do about it.',
      },
      {
        type: 'paragraph',
        content:
          'I am not making a moral point. The tools are useful, free, instant, and require no IT approval. When deadlines are tight and the alternative is reading 800 pages by hand, of course people reach for them. The problem is what happens to the data the moment it is pasted.',
      },
      {
        type: 'heading2',
        content: 'Where the data actually goes',
      },
      {
        type: 'paragraph',
        content:
          'When a paragraph from a clinical record is pasted into the consumer version of ChatGPT, several things happen, none of them visible to the person doing the pasting.',
      },
      {
        type: 'paragraph',
        content:
          'The text leaves the device and is sent to OpenAI’s servers, which sit in the United States. Under OpenAI’s consumer terms (free and Plus tiers, by default), conversations are retained and may be used to improve future models, unless the user has explicitly opted out. The conversation is associated with the user’s account and stored on infrastructure that is not subject to UK data-protection law in the same way a UK-resident system would be.',
      },
      {
        type: 'paragraph',
        content:
          'The same is broadly true of consumer Claude (Anthropic), Gemini (Google), Copilot in its consumer tier, and the various Mistral and Llama-based assistants. Each has different defaults, different retention policies, different opt-out mechanisms. None of them was designed with special-category personal data in mind.',
      },
      {
        type: 'paragraph',
        content:
          'Enterprise tiers are different. OpenAI Enterprise and the API both contractually exclude customer data from training. Microsoft Azure OpenAI offers UK regional deployments. Anthropic’s API is no-training by default. These are real, careful products. They are also not the products people open on their phones at lunchtime.',
      },
      {
        type: 'paragraph',
        content:
          'The regulators have been clear about the risk. In March 2023, the Italian data-protection authority temporarily banned ChatGPT for breaches of GDPR around lawful basis, transparency and the handling of personal data. OpenAI made changes and the ban was lifted, but in December 2024 the Garante imposed a €15 million fine on the same grounds. The ICO’s 2024 guidance on generative AI and data protection cautions specifically against the use of consumer LLMs for special-category data. The most public corporate failure remains Samsung in April 2023, where engineers pasted proprietary source code and meeting notes into ChatGPT and the data left the company’s control. Samsung banned consumer generative AI for internal work within weeks. JPMorgan Chase, Verizon and several others have done the same on the same grounds.',
      },
      {
        type: 'heading2',
        content: 'What this means for medico-legal work',
      },
      {
        type: 'paragraph',
        content:
          'For most professions, this is a confidentiality and intellectual-property issue. For medico-legal work, it is something more.',
      },
      {
        type: 'paragraph',
        content:
          'Medical records are special-category personal data under Article 9 of the UK GDPR. Special-category processing requires both a lawful basis under Article 6 and a separate Article 9 condition; for litigation, usually 9(2)(f), the establishment, exercise or defence of legal claims. None of those conditions relax the underlying obligations. Encryption, access control, retention limits and a documented Data Protection Impact Assessment are still required.',
      },
      {
        type: 'paragraph',
        content:
          'When a solicitor pastes a discharge summary into a consumer chatbot, those obligations do not pause. The processing is taking place. The data is leaving the firm’s control. There is no DPIA. There is no audit trail. The firm cannot tell a regulator what was sent, when, by whom, or what came back. If a patient subject access request later asks where their data has been processed, the firm cannot honestly answer.',
      },
      {
        type: 'paragraph',
        content:
          'The professional duties go further still. SRA Principle 7 requires solicitors to act in each client’s best interests, which the SRA’s 2023 risk outlook on technology has interpreted to include using only systems that protect client information. The GMC’s Good Medical Practice (2024) requires doctors to maintain confidentiality and to use only secure systems for patient information. The Bar Standards Board has issued similar guidance for barristers.',
      },
      {
        type: 'paragraph',
        content:
          'And then there is Ayinde, which I wrote about in the previous article. The High Court has already made plain that the duty to verify AI output cannot be delegated. The duty of confidentiality is older, deeper, and equally non-delegable. Both run in the same direction: a tool you cannot inspect, audit or control is a tool you cannot lawfully use for client work.',
      },
      {
        type: 'heading2',
        content: 'Why people do it anyway',
      },
      {
        type: 'paragraph',
        content:
          'It would be easy to write the rest of this article as a finger-wag. I do not think that is useful, because the people doing this are not negligent. They are responding to a tooling gap.',
      },
      {
        type: 'paragraph',
        content:
          'The unaided alternative — reading the bundle by hand at 11pm after a full caseload — is genuinely worse for the client than getting some help. The expert who asks ChatGPT to plain-English a discharge summary at 7am before clinic is not trying to leak data. They are trying to do their job within the time available. The free tools are right there, on every device, with no friction.',
      },
      {
        type: 'paragraph',
        content:
          'Procurement has not caught up. Many firms have an “AI policy” that consists of a paragraph in the staff handbook saying not to use consumer AI for client work. The policy is unenforceable, the supervision is patchy, and the workflow it implicitly demands (back to manual reading) is not realistic. The result is shadow AI use on a scale most managing partners would not want to admit.',
      },
      {
        type: 'callout',
        variant: 'insight',
        content:
          'The honest position is that the profession needs alternatives, not warnings. Professionals will reach for AI; the question is which AI. The job of vendors and procurement teams is to make sure the responsible alternative is at least as easy to use as the consumer one.',
      },
      {
        type: 'heading2',
        content: 'What a responsible alternative looks like',
      },
      {
        type: 'paragraph',
        content:
          'Five things distinguish an AI tool that is safe to use for client work from one that isn’t. None is a marketing claim. All are answerable in writing.',
      },
      {
        type: 'ordered',
        items: [
          'Where the data lives — Data should be processed and stored within the UK, in infrastructure subject to UK law, with no transfers to other jurisdictions without explicit safeguards. “Global” is a red flag; “EU-equivalent” is not the same as UK.',
          'Whether the data trains the model — The vendor should be able to point to a contractual clause confirming that customer data is excluded from any training, fine-tuning or evaluation. Default consumer tiers usually fail this test. Enterprise tiers and APIs typically pass it.',
          'An audit trail — The system should keep a record of what was uploaded, what was asked, what was returned, by whom and when. Not for surveillance, but for the moment a regulator, a client or a court asks what happened on a given date.',
          'Access control — Multi-factor authentication, role-based access, and the ability to revoke access in one click when a colleague leaves. Unglamorous controls; also the ones that fail first when something goes wrong.',
          'A DPIA the firm can use — The vendor should make its own data-protection impact assessment available, so a firm’s DPO can rely on it without re-running every question from scratch.',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Any vendor that bristles at being asked these five questions is telling you something useful about how seriously they take the duty.',
      },
      {
        type: 'heading2',
        content: 'A practical line',
      },
      {
        type: 'paragraph',
        content: 'The line I would draw, if asked, is this.',
      },
      {
        type: 'paragraph',
        content:
          'For genuinely non-sensitive work — a generic question about NICE guidance phrasing, a query about a public legal principle, a draft of an internal email — the consumer tools are fine, with the same caution you would apply to any cloud service.',
      },
      {
        type: 'paragraph',
        content:
          'For anything that contains client information, patient data, or material covered by professional confidence, do not paste. Use a tool that has been bought, contracted for and risk-assessed by your firm. If your firm does not have one, that is a problem to escalate, not work around.',
      },
      {
        type: 'paragraph',
        content:
          'This is not a counsel of perfection. It is the same line the profession has always drawn between work email and personal email, between firm laptops and home laptops, between the case-management system and a Word document on a USB stick. The technology is new; the principle is not.',
      },
      {
        type: 'paragraph',
        content:
          'The interesting AI question for the profession is no longer whether to use AI. It is whose AI to use, and on what terms. The vendors who do this responsibly will be inspectable, accountable and visible — the principles I argued for in the earlier piece on responsible AI architecture. The ones who do not will continue to be useful, free, and silently dangerous.',
      },
      {
        type: 'paragraph',
        content:
          'If you are a partner or a clinical director reading this, the question to ask is not whether your team uses AI. They do. The question is what the firm or the practice is offering them as the responsible alternative.',
      },
    ],
  },

  // ── AI isn't a black box. That's a design choice. ───────────────────
  {
    slug: 'ai-not-a-black-box',
    title: 'AI isn’t a black box. That’s a design choice.',
    subtitle:
      'How a single fact travels from a scanned page to a chronology entry, and why we don’t make the clinical or legal call at the end of it.',
    category: 'Responsible AI',
    categoryColor: 'blue',
    publishedDate: '2026-05-05',
    readingTime: '8 min read',
    author: 'Ahmmad Youssef, PhD',
    authorRole: 'Technical Co-founder, ClinLexis',
    heroIcon: 'brain-circuit',
    ogImage: '/og/ai-not-a-black-box.png',
    sections: [
      {
        type: 'paragraph',
        content:
          'When people say AI is a “black box”, they usually mean something specific: you put a question in at one end, an answer comes out the other, and what happens in the middle is hidden from view. You are being asked to trust a result without being able to see how it was reached. For solicitors, doctors and expert witnesses, whose work depends on being able to defend their reasoning, that is a serious concern, and an entirely reasonable one.',
      },
      {
        type: 'paragraph',
        content:
          'It is not, however, the whole picture. The black-box framing fits a single AI model in isolation. A product built around a model is a different thing: it is a pipeline of separate steps, and each step can either hide its working or show it. Whether those steps are visible or opaque is one of the most important decisions an AI vendor makes. This article is about why visibility matters more in medico-legal work than almost anywhere else, and what we have done about it at ClinLexis.',
      },
      {
        type: 'paragraph',
        content:
          'When Dr Abdel-Aziz and I started talking about how ClinLexis would actually work, the question I kept returning to was not whether the AI would be capable enough. It was whether it would be inspectable enough. Could a clinician or a solicitor, sitting in front of a screen full of AI-generated chronology entries, ever satisfy themselves that they could trust what they were looking at? Most of what we have built since is an answer to that question.',
      },
      {
        type: 'heading2',
        content: 'Where black-box AI isn’t accepted already',
      },
      {
        type: 'paragraph',
        content:
          'The principle that AI should be inspectable, not just impressive, is not new, and it isn’t unique to medico-legal work. Several other domains have already drawn the line.',
      },
      {
        type: 'paragraph',
        content:
          'AI used directly in patient care is the closest parallel. In the UK, the MHRA regulates it as it would any other clinical tool, and explainability is part of what developers have to demonstrate before deployment. UK GDPR Article 22 reinforces this from the patient’s side, giving anyone subject to a solely automated medical decision the right to “meaningful information about the logic involved” and to human intervention. NHS England’s Code of conduct for data-driven health and care technology goes further still, requiring AI systems used in NHS care to be transparent in operation. When a radiology tool flags a region on a mammogram, the clinician sees the highlight, the confidence score and the visual overlay, not just a verdict. The FDA has now cleared more than 950 AI- and machine-learning-enabled clinical tools on those terms.',
      },
      {
        type: 'paragraph',
        content:
          'Financial services drew the same line earlier. The FCA’s Consumer Duty obliges firms to support customer understanding of decisions affecting them, and the Bank of England’s supervisory statement on model risk management (SS1/23, in force from May 2024) requires firms to evidence the interpretability of their AI and machine-learning models. Banks have spent the past decade investing in interpretability: counterfactuals, feature attributions, model risk frameworks. Regulators were never going to accept “the algorithm declined the loan” as a final answer.',
      },
      {
        type: 'paragraph',
        content:
          'Reasoning models themselves have moved on. The newest large language models now expose their intermediate reasoning rather than only the final answer, and national AI safety institutes, including the UK’s AI Security Institute, are publishing evaluations of how faithfully those traces reflect what the model actually does. The direction of travel across the industry is visibility, not opacity.',
      },
      {
        type: 'paragraph',
        content:
          'Medico-legal practice should expect the same standard. The verification duty is explicit, the data is the most sensitive there is, and the consequences of being wrong end up in court.',
      },
      {
        type: 'heading2',
        content: 'Why this matters',
      },
      {
        type: 'paragraph',
        content: 'Two recent findings show why.',
      },
      {
        type: 'paragraph',
        content:
          'The first is the 2024 Stanford HAI study by Magesh and colleagues. They tested the leading legal-research tools, including ones marketed as “hallucination-free”, and found error rates between 17% and 33% on retrieval-augmented systems built on curated case-law databases. Lexis+ AI was at the better end; Westlaw’s AI-Assisted Research was at the worse. These were paid, professional-grade tools sold to qualified solicitors. The vendors were not lying about the underlying architecture. They were over-promising about what the architecture could guarantee.',
      },
      {
        type: 'paragraph',
        content:
          'The second is Ayinde and the parallel matter Al-Haroun, both decided in the High Court in 2025. Solicitors and a barrister had submitted skeleton arguments containing fabricated case citations, generated by AI tools and never verified. The court could have treated this as a novel problem requiring new doctrine. Instead, it applied an old rule with full force: a lawyer who signs a document is responsible for its contents, AI tool or no AI tool. Wasted-costs orders and regulatory referrals followed.',
      },
      {
        type: 'paragraph',
        content:
          'Take those two findings together and the design objective for any serious AI tool in this domain becomes clear. The duty to verify cannot be delegated to a machine. So the system has to make verification fast, obvious and unavoidable. Every claim the system makes about a clinical record has to be one click from the page that produced it. If that click is hard, or imprecise, or absent, the system has done a bad job, no matter how clever the underlying model.',
      },
      {
        type: 'heading2',
        content: 'One fact, end to end',
      },
      {
        type: 'paragraph',
        content:
          'Most of what we built at ClinLexis is a long argument with that design objective. The clearest way to show it is to follow a single fact through the system, from a scanned page to the screen.',
      },
      {
        type: 'scenario',
        content:
          'The document in front of you is a 47-page hospital discharge summary, scanned at a GP surgery, slightly skewed. Buried on page 19 is a sentence: “Lithium level taken 12/03/2024, result 0.4 mmol/L, no review documented.” An expert needs to know about this entry. Here is what happens between the PDF and the chronology row that surfaces it.',
      },
      {
        type: 'ordered',
        items: [
          'OCR with coordinates — Before any language model is involved, we run the page through optical character recognition that returns, for every word, the coordinates of its bounding box on the page. The text is not “extracted by the AI”; it is read off the pixels by a deterministic process that records exactly where each word sat. Those coordinates are stored alongside the text and travel with it through the rest of the pipeline. They are how we will find the words again later.',
          'A multi-pass pipeline, not a single model — This is the part that surprises people most. The popular image of generative AI is one giant model being asked one giant question. That is not how a careful system works. Inside ClinLexis, a single page is processed by several passes, each with a defined input and output: a classification pass that decides what kind of document this is; an extraction pass that picks out clinical events and their dates; a structuring pass that places them on the timeline; a linking pass that matches each event back to its source coordinates; and where appropriate, a reasoning pass that flags possible significance. Each pass can be inspected, replayed, and disagreed with. None of them is asked to “do everything”. It is unglamorous, multi-stage engineering. It is also why the failure modes of the system are observable rather than mysterious.',
          'A citation that opens the page, not just the document — When the lithium-level entry appears in the chronology, the citation next to it is not an aspiration. It is a link to the exact bounding box on page 19 of the discharge summary. Clicking it opens the PDF in our viewer with the sentence highlighted. Not the page — the sentence. If the highlight does not appear, we treat that as a bug and log it; we have a service whose only job is to record citation failures and feed them back into the pipeline.',
          'A “show your working” button on every entry — Next to each chronology entry sits a small lightbulb. Click it, and the system explains, in plain English, how the entry was assembled: which page it was drawn from, what the source text actually says, and how the date was resolved. You do not have to take the chronology on faith.',
          'Inconsistency flags that point both ways — If the system finds that another document in the bundle records that the level was reviewed, it surfaces the contradiction, with citations to both sources. You see the conflict, you see the evidence on each side, and you decide which one is right.',
        ],
      },
      {
        type: 'paragraph',
        content:
          'That is the whole architecture in one example. Five layers of transparency, each independently verifiable. None of it is magic. All of it is engineering choices we could have skipped.',
      },
      {
        type: 'heading2',
        content: 'The line we won’t cross',
      },
      {
        type: 'paragraph',
        content:
          'There is a deliberate choice running through every step above, and it is the most important one. The system surfaces evidence; it does not make the call.',
      },
      {
        type: 'paragraph',
        content:
          'That sounds like a marketing line. It is a structural commitment that constrains everything we build. ClinLexis is not allowed, by design, to assert a clinical opinion or a legal conclusion. It will tell you that a review interval was 23 days. It will not tell you that 23 days was negligent. It will tell you that two records say different things about a medication. It will not tell you which one is right. It will tell you what NICE recommends. It will not tell you that the recommendation was breached. The language of the system is consistently neutral, and we have rejected feature ideas, some of them genuinely useful, because they would have crossed that line.',
      },
      {
        type: 'callout',
        variant: 'warning',
        content:
          'The reason is simple. Clinical and legal judgement are professional activities that carry professional accountability. A consultant signs a report; a solicitor signs a pleading; a court holds them responsible. If a piece of software starts generating those judgements, it is taking on responsibility it has no way to discharge, and quietly transferring it to whoever clicked “approve”. That is not responsible AI. That is responsibility laundering.',
      },
      {
        type: 'paragraph',
        content:
          'Drawing this line has consequences for how the product feels. Our chronology screens have a slightly clinical quality; they show data and let you reason. Our reports come with placeholders for opinion, not opinion itself. Some prospective customers find this less impressive than tools that produce confident-sounding conclusions on demand. We think the absence of confident-sounding conclusions is a feature. The point of a glass-box system is not just that you can see what it did; it is that you can see what it refused to do.',
      },
      {
        type: 'heading2',
        content: 'Three questions to ask any AI vendor',
      },
      {
        type: 'paragraph',
        content:
          'If you are a solicitor, an expert witness or a clinician evaluating an AI tool, you do not need to learn the engineering to ask useful questions. Three are usually enough.',
      },
      {
        type: 'ordered',
        items: [
          'Source in one click? — When the system makes a claim about a record, can you reach the source — the exact passage on the exact page — in one click? If not, the verification burden the courts have placed on you has not been engineered for. It has been pushed back onto you.',
          'Visible reasoning? — When the system flags something as significant, can you see why, in plain English, with the guideline or the contradictory record cited? If the reasoning is not visible, the system is not glass.',
          'Where does the system stop? — If it offers clinical or legal conclusions, ask who is accountable for them. If it surfaces evidence and stops short of the call, you are looking at a tool that respects what you do.',
        ],
      },
      {
        type: 'paragraph',
        content:
          'If the answers come back specific and technical, the tool has been built with the duty of verification in mind. Vague answers tell you it hasn’t.',
      },
    ],
  },  // ── What AI-Assisted Medical Record Analysis Actually Looks Like ──────
  {
    slug: 'ai-record-analysis-in-practice',
    title:
      'What AI-assisted medical record analysis actually looks like in practice',
    subtitle:
      'From manual extraction to structured chronology, source-linked traceability, record interrogation and drafted output — what meaningful AI assistance changes about the medico-legal workflow.',
    category: 'Practitioner Perspective',
    categoryColor: 'amber',
    publishedDate: '2026-05-05',
    readingTime: '8 min read',
    author: 'Dr Khaled Abdel-Aziz PhD FRCP',
    authorRole: 'Consultant Neurologist & Medical Co-founder, ClinLexis',
    heroIcon: 'calendar-clock',
    ogImage: '/og/ai-record-analysis-in-practice.png',
    sections: [
      {
        type: 'paragraph',
        content:
          'In discussions around artificial intelligence in medico-legal work, the conversation often remains at a high level. There is general agreement that the volume of medical records is increasing, that manual workflows are under pressure, and that technology may have a role to play. However, what is less often clear is what that actually looks like in day-to-day practice.',
      },
      {
        type: 'paragraph',
        content:
          'From a practical perspective, the question is straightforward: how does AI meaningfully assist with the review and analysis of medical records in a real case?',
      },
      {
        type: 'heading2',
        content: 'Starting point: the same problem',
      },
      {
        type: 'paragraph',
        content: 'The starting point remains unchanged.',
      },
      {
        type: 'paragraph',
        content:
          'A set of medical records is received — often large, frequently unstructured, and typically drawn from multiple sources. These may include hospital notes, GP records, correspondence, therapy records, imaging reports and occupational health documentation.',
      },
      {
        type: 'paragraph',
        content:
          'Before any analysis can begin, there is a need to understand:',
      },
      {
        type: 'bullets',
        items: [
          'The sequence of events',
          'The relevant clinical interactions',
          'The progression of symptoms and treatment',
          'The relationship between different parts of the record',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Traditionally, this requires a manual process of reading, extracting, and organising information. The question is how that process can be supported more effectively.',
      },
      {
        type: 'heading2',
        content: 'Structured chronology as the foundation',
      },
      {
        type: 'paragraph',
        content:
          'One of the most immediate applications of AI in this context is the generation of structured chronologies.',
      },
      {
        type: 'paragraph',
        content:
          'Rather than building a timeline manually, the platform analyses the records and organises events into a chronological framework, grouping related entries and presenting them in a format that can be reviewed and refined.',
      },
      {
        type: 'paragraph',
        content:
          'From a user perspective, this does not remove the need for oversight. The chronology still requires review and interpretation. However, it changes the starting point.',
      },
      {
        type: 'paragraph',
        content:
          'Instead of working from a blank page, the medico-legal professional begins with a structured representation of the records, allowing them to focus on validation, refinement and analysis rather than initial extraction.',
      },
      {
        type: 'heading2',
        content: 'Traceability and source linking',
      },
      {
        type: 'paragraph',
        content:
          'A key consideration in medico-legal work is not simply what is presented, but how it is supported. For that reason, any meaningful application of AI in this space must allow the user to verify outputs against the underlying records.',
      },
      {
        type: 'paragraph',
        content:
          'In practice, this means that each chronology entry or extracted point is linked directly back to its source. This allows users to move seamlessly between structured summaries and the original documentation, ensuring that any interpretation remains grounded in the evidence.',
      },
      {
        type: 'paragraph',
        content:
          'This traceability is essential. Without it, the outputs would have limited utility in a setting where accuracy and defensibility are critical.',
      },
      {
        type: 'heading2',
        content: 'Interrogating the record',
      },
      {
        type: 'paragraph',
        content:
          'Beyond chronology generation, another area where AI can assist is in the interrogation of records. In complex cases, a significant amount of time is spent searching for specific pieces of information:',
      },
      {
        type: 'bullets',
        items: [
          'When did symptoms first appear?',
          'What treatment was provided at a particular stage?',
          'Is there evidence of delay or deterioration?',
        ],
      },
      {
        type: 'paragraph',
        content:
          'Rather than manually locating this information, it is possible to query the record directly. From a practical standpoint, this involves asking targeted questions and receiving structured responses based on analysis of the underlying material.',
      },
      {
        type: 'paragraph',
        content:
          'This does not replace the need for professional judgement. The responses require interpretation and verification. However, it can significantly reduce the time required to locate relevant information and to gain an overview of key issues within a large dataset.',
      },
      {
        type: 'heading2',
        content: 'From analysis to drafting',
      },
      {
        type: 'paragraph',
        content:
          'Then comes the transition from record review to report writing. Once the records have been analysed and a chronology established, the next step is to translate that understanding into a structured report.',
      },
      {
        type: 'paragraph',
        content: 'This can involve:',
      },
      {
        type: 'bullets',
        items: [
          'Summarising key events',
          'Describing clinical progression',
          'Presenting findings in a consistent format',
        ],
      },
      {
        type: 'paragraph',
        content:
          'In practice, this often means revisiting the same material and reworking it into a different structure. Where structured analysis is already available, it becomes possible to support this process more directly.',
      },
      {
        type: 'paragraph',
        content:
          'Draft outputs can be generated based on the underlying structured data, providing a starting point for further refinement. These outputs can be aligned with established formats, including user-defined templates, ensuring consistency with existing reporting practices.',
      },
      {
        type: 'paragraph',
        content:
          'As with other stages, the role of the professional remains central. The draft is reviewed, edited, and shaped into a final report. The difference now is that the initial construction of the document becomes more efficient.',
      },
      {
        type: 'heading2',
        content: 'Maintaining professional control',
      },
      {
        type: 'paragraph',
        content:
          'A consistent concern when discussing AI in medico-legal work is the risk of over-reliance on automated outputs. In practice, effective use of these tools depends on maintaining clear boundaries.',
      },
      {
        type: 'paragraph',
        content: 'The role of the platform is to support:',
      },
      {
        type: 'bullets',
        items: [
          'Extraction of information',
          'Structuring of records',
          'Initial drafting',
        ],
      },
      {
        type: 'paragraph',
        content: 'It does not replace:',
      },
      {
        type: 'bullets',
        items: [
          'Clinical interpretation',
          'Legal analysis',
          'Expert opinion',
        ],
      },
      {
        type: 'paragraph',
        content:
          'In that sense, the technology functions as an extension of the workflow rather than a substitute for it.',
      },
      {
        type: 'heading2',
        content: 'A change in emphasis',
      },
      {
        type: 'paragraph',
        content:
          'What emerges from this is not a fundamentally different process, but a shift in emphasis.',
      },
      {
        type: 'paragraph',
        content: 'Less time is spent:',
      },
      {
        type: 'bullets',
        items: [
          'Locating information',
          'Constructing basic structures',
          'Repeating manual tasks',
        ],
      },
      {
        type: 'paragraph',
        content: 'More time is spent:',
      },
      {
        type: 'bullets',
        items: [
          'Interpreting clinical detail',
          'Assessing significance',
          'Formulating opinions',
        ],
      },
      {
        type: 'paragraph',
        content:
          'From a professional perspective, this is where expertise is most valuable.',
      },
      {
        type: 'heading2',
        content: 'A practical perspective',
      },
      {
        type: 'paragraph',
        content:
          'From experience, one of the most challenging aspects of medico-legal work is not the analysis itself, but the preparation required to reach the point where analysis can begin. Any approach that makes that preparatory stage more efficient has the potential to improve both the quality and the timeliness of the work that follows.',
      },
      {
        type: 'paragraph',
        content:
          'The question is not whether these tools can produce output — it is whether they can support the way professionals already think and work.',
      },
      {
        type: 'heading2',
        content: 'Looking ahead',
      },
      {
        type: 'paragraph',
        content:
          'As with any technological shift, adoption will depend on trust, usability, and demonstrable value in real-world cases. At ClinLexis we have addressed that by implementing an introductory low monthly fee of £24.99 + VAT to lower the barrier to getting started, and are currently offering 1,000 free credits on sign-up to new users to sample the usability and outputs of the platform.',
      },
      {
        type: 'heading2',
        content: 'Final thought',
      },
      {
        type: 'paragraph',
        content:
          'In many ways, the most useful way to think about AI in the context of medical record analysis is not as a replacement for existing processes, but as a way of improving how those processes begin. If the starting point is clearer, more structured, and easier to interrogate, the work that follows becomes more focused — and in medico-legal practice, that focus is where real value lies.',
      },
    ],
  },
];
